import React, {
    useState, useCallback, useMemo, useEffect,
} from 'react';
import Tippy from '@tippyjs/react';
import { createPortal } from 'react-dom';
import { Link } from '@reach/router';
import { PAGE_VISIBILITY, TAG_SOURCE } from '../../utilities/CONSTANTS';
import { getLocalStorageWithExpiry, setLocalStorageWithExpiry } from '../../utilities/localStorageUtilities';
import { getApiPageGenerateTagsUrl, getPageSearchUrl, getApiPageTagUrl } from '../../utilities/urlHelper';
import AddTagMenu from './AddTagMenu';
import { hasKaiFeature } from '../../utilities/featureHelper';

const GenerateTagsSection = ({ pageId, pageContentLength }) => {
    const [isGeneratingTags, setIsGeneratingTags] = useState(false);
    const storedAiTagsGeneration = getLocalStorageWithExpiry(`ai_tags_${pageId}`);
    const EXPIRY_TIME = 10; // minutes

    const generateTags = () => {
        setIsGeneratingTags(true);
        setLocalStorageWithExpiry(`ai_tags_${pageId}`, new Date().valueOf(), EXPIRY_TIME);
        const url = getApiPageGenerateTagsUrl(pageId);
        $.ajax({
            type: 'POST',
            url,
        }).catch(console.error);
    };

    if (isGeneratingTags || storedAiTagsGeneration) {
        return (
            <p className="m0">Tags are being generated. Please check back in a few minutes...</p>
        );
    }

    return (
        <>
            { pageContentLength > 0 && pageContentLength < window._min_word_classification
                && <p className="m0 mb2">I didn't generate tags on this page based on its content because it's so short. But I can if you'd like. Should I do that now?</p>}

            { pageContentLength > 0 && pageContentLength >= window._min_word_classification
                && <p className="m0 mb2">I haven't generated tags on this page yet. Should I do that now?</p>}

            { pageContentLength === 0
                && <p className="m0 mb2">I need some content before I can generate tags.</p>}

            { pageContentLength > 0
                && (
                    <button
                        className="btn btn-primary"
                        onClick={generateTags}
                        type="button"
                    >
                Generate tags
                    </button>
                )}
        </>
    );
};

const TagListElement = ({ tag, removeTagById }) => {
    const { id, name, source } = tag;
    const tagLink = getPageSearchUrl(name);

    return (
        <li className="page-insight-item">
            <a className="flex-auto" href={tagLink}>{name}</a>
            <TagDeleteButton tagId={id} removeTagById={removeTagById} />
        </li>
    );
};

const TagDeleteButton = ({ tagId, removeTagById }) => (
    <Tippy content="Remove tag" placement="left">
        <button
            className="page-insight-delete"
            onClick={() => removeTagById(tagId)}
            aria-label="Remove tag"
            type="button"
        >
            &times;
        </button>
    </Tippy>
);

const TabBar = ({ selectedTab, kaiTagsCount = 0, teamTagsCount = 0 }) => (
    <div className="tab-nav mb2">
        <Link to="?tags-tab=team">
            <div
                className={`tab b ${selectedTab === 'team' ? 'active' : ''}`}
                type="button"
            >
                By your Team
                <span className="tab-nav-count">{teamTagsCount}</span>
            </div>
        </Link>
        {hasKaiFeature() && (
            <Link to="?tags-tab=kai">
                <div
                    className={`tab a ${selectedTab === 'kai' ? 'active' : ''}`}
                    type="button"
                >
                    By Kai
                    <span className="tab-nav-count">{kaiTagsCount}</span>
                </div>
            </Link>
        )}
    </div>
);

const PageGeneratedTagsModal = ({
    tags, closeModal, pageContentLength, pageVisibility, pageId, updateTags,
}) => {

    const [tagList, setTagList] = useState(tags || []);
    const [isPrivatePage] = useState(pageVisibility === PAGE_VISIBILITY.PRIVATE);
    const [selectedTab, setSelectedTab] = useState(hasKaiFeature() ? 'kai' : 'team');
    const [kaiTags, setKaiTags] = useState(tags.filter(tag => tag.source !== TAG_SOURCE.TEAM) || []);
    const [teamTags, setTeamTags] = useState(tags.filter(tag => tag.source === TAG_SOURCE.TEAM) || []);
    const [availableTags, setAvailableTags] = useState(tags.filter(tag => tag.source === TAG_SOURCE.TEAM) || []);

    useEffect(() => {
        setKaiTags(tagList.filter(tag => tag.source !== TAG_SOURCE.TEAM) || []);
        setTeamTags(tagList.filter(tag => tag.source === TAG_SOURCE.TEAM) || []);
        setAvailableTags(tagList.filter(tag => tag.source === TAG_SOURCE.TEAM) || []);
    }, [tagList]);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const tab = params.get('tags-tab');
        setSelectedTab(tab === 'kai' ? 'kai' : 'team');
    }, [window.location.search]);

    const onClickModal = useCallback(({ target }) => {
        const className = target.getAttribute('class');
        if (closeModal && className && className.includes('react-modal open')) {
            closeModal();
        }
    }, [closeModal]);

    const removeTagById = useCallback((id) => {
        const url = getApiPageTagUrl(pageId, id);
        $.ajax({
            type: 'DELETE',
            url,
        })
            .then(({ available_page_tags }) => {
                if (available_page_tags) {
                    setAvailableTags(available_page_tags);
                }
            })
            .catch(console.error);

        const updatedTags = tagList.filter(tag => (tag.id !== id));
        setTagList(updatedTags);
        updateTags(updatedTags);
    }, [tagList, pageId, updateTags]);

    const kaiTagListElements = useMemo(
        () => kaiTags.map(tag => (
            <TagListElement
                key={tag.id}
                tag={tag}
                removeTagById={removeTagById}
            />
        )),
        [kaiTags, removeTagById],
    );

    const teamTagListElements = useMemo(
        () => teamTags.map(tag => (
            <TagListElement
                key={tag.id}
                tag={tag}
                removeTagById={removeTagById}
            />
        )),
        [teamTags, removeTagById],
    );

    return createPortal(
        <div className="react-modal open" id="page-tags-modal" onMouseDown={onClickModal}>
            <div className="modal-content p2">
                <h2>Tags</h2>

                <TabBar selectedTab={selectedTab} kaiTagsCount={kaiTags.length} teamTagsCount={teamTags.length} />

                {selectedTab === 'kai' && hasKaiFeature()
                    ? (
                        <>
                            <div className="bg-light-blue border border-blue rounded p2 mb2">
                                <div className="flex">
                                    <div className="flex-none mr2 sm-show">
                                        <a href={window.support_link_ai_features} target="_blank" rel="noopener noreferrer">
                                            <Tippy content="Kai" placement="bottom">
                                                <img
                                                    className="bot-avatar"
                                                    src="/img/kai.png"
                                                    width="28"
                                                    height="28"
                                                    alt="Kai"
                                                    title="Kai"
                                                />
                                            </Tippy>
                                        </a>

                                    </div>
                                    <div className="flex-auto">
                                        <div>
                                            <span className="bold">Kai</span>
                                            <span className="ml1 tag tag-small navy bg-blue align-text-bottom">AI</span>
                                        </div>
                                        {(kaiTags.length === 0)
                                            ? isPrivatePage
                                                ? <p className="m0">I can't generate insights for this page because it's in a private category.</p>
                                                : <GenerateTagsSection pageId={pageId} pageContentLength={pageContentLength} />
                                            : (
                                                <p className="m0">Based on the content of this page, here's some tags.</p>
                                            )}
                                    </div>
                                </div>
                            </div>
                            {(!isPrivatePage && (kaiTags.length > 0))
                                && (
                                    <ul className="page-insights-list" id="page-questions-list">
                                        {kaiTagListElements && kaiTagListElements.length > 0
                                            ? kaiTagListElements
                                            : ''}
                                    </ul>
                                )}
                        </>
                    )
                    : (
                        <>
                            <ul className="page-insights-list" id="page-answered-questions-list">
                                {teamTagListElements && teamTagListElements.length > 0
                                    ? teamTagListElements
                                    : 'No team tags found.'}
                            </ul>

                            <AddTagMenu
                                pageId={pageId}
                                tags={tags}
                                availableTags={availableTags}
                                onTagsAdded={(newTags) => {
                                    setTagList(newTags);
                                    updateTags(newTags);
                                }}
                                closeModal={closeModal}
                            />
                        </>
                    )}
            </div>
        </div>,
        document.body,
    );
};

export default PageGeneratedTagsModal;
